import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Hand, ListTodo, Mailbox, Radar } from "lucide-mui";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import CountryName from "../../components/CountryName.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import FilterBar from "../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { useFilters } from "../../components/filters/useFilters.ts";
import KpiTile from "../../components/KpiTile.tsx";
import PageHeader from "../../components/PageHeader.tsx";
import Panel from "../../components/Panel.tsx";
import RelativeTime from "../../components/RelativeTime.tsx";
import { hitKindIcon, hitKindLabel } from "../../components/prospects/hitKinds.tsx";
import { HIT_KIND_COLORS, hitTimelinePoints } from "../../components/prospects/hitTimeline.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { useUrlState } from "../../hooks/useUrlState.ts";
import { periodFrom, periodLabel, periodSpec, sinceFor } from "../../utils/periods.ts";
import SegmentedBar from "../../components/charts/SegmentedBar.tsx";
import { formatNumber, utcDayLabel } from "../../utils/format.ts";
import StatusChip from "../../components/StatusChip.tsx";

/**
 * Where the tenant's prospects stand: what needs a decision, what is being
 * worked, what was finished, who has been finding them and how often. Every
 * number opens the queue behind it.
 */
export default function ProspectsOverviewPage() {
  const { selectedTenantId, selectedTenant, tenantPath } = useTenant();
  const url = useUrlState();
  const period = periodFrom(url.searchParams.get("period"));
  const since = sinceFor(period);
  const periodName = periodLabel(period).toLowerCase();

  const { data, isLoading } = useQuery({
    queryKey: keys.prospect.dashboard(selectedTenantId, since ?? null),
    queryFn: () => api.getProspectDashboard(selectedTenantId!, since),
    enabled: !!selectedTenantId,
    meta: { live: true },
  });

  const specs = useMemo<FilterSpec[]>(() => [periodSpec()], []);
  const filters = useFilters(specs, { state: url });
  const tenantName = selectedTenant?.name ?? "the tenant";
  const link = (page: "inbox" | "worklist" | "done", params: Record<string, string> = {}) =>
    tenantPath(`/prospects/${page}${Object.keys(params).length ? `?${new URLSearchParams(params)}` : ""}`);

  const points = data ? hitTimelinePoints(data.timeline, data.timeline_bucket, since) : [];
  const needsYou = data?.queues.inbox ?? 0;
  const kinds = data?.by_kind ?? [];
  const kindOf = (k: "signal" | "agent" | "manual") => kinds.find((b) => b.kind === k);

  return (
    <>
      <PageHeader
        title="Prospects"
        description={`Where ${tenantName}'s prospects stand, and what has been finding them`}
        actions={
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to={link("inbox")} variant="contained" startIcon={<Mailbox />}>
              Open inbox
            </Button>
            <Button component={RouterLink} to={link("worklist")} variant="outlined" startIcon={<ListTodo />}>
              Worklist
            </Button>
          </Stack>
        }
      />
      <Box sx={{ mb: 2 }}>
        <FilterBar filters={filters} variant="inline" />
      </Box>
      {isLoading && <CenteredSpinner />}
      {data && data.hits_in_period === 0 && data.queues.inbox + data.queues.worklist + data.queues.done === 0 && (
        <EmptyState
          variant="panel"
          icon={<Radar />}
          title="No prospects yet"
          description="Companies land here as soon as a signal, an agent or someone on a collection finds them."
        />
      )}
      {data && (data.hits_in_period > 0 || data.queues.inbox + data.queues.worklist + data.queues.done > 0) && (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <KpiTile
                label="Needs a decision"
                icon={needsYou ? <Hand /> : <CircleCheck />}
                color={needsYou ? "warning" : "success"}
                value={needsYou}
                caption={needsYou ? `${formatNumber(data.queues.reopened)} reopened by a new hit` : "Inbox zero"}
                to={link("inbox")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <KpiTile
                label="Pursuing"
                icon={<ListTodo />}
                color="success"
                value={data.queues.worklist}
                caption={data.queues.snoozed ? `${formatNumber(data.queues.snoozed)} snoozed, back later` : "On the worklist"}
                to={link("worklist")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <KpiTile
                label={`Done · ${periodName}`}
                icon={<CircleCheck />}
                color="primary"
                value={data.queues.done_in_period}
                caption={`${formatNumber(data.queues.done)} all time`}
                to={link("done")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <KpiTile
                label={`Hits · ${periodName}`}
                icon={<Radar />}
                color="primary"
                value={data.hits_in_period}
                caption={`on ${formatNumber(data.prospects_hit_in_period)} ${data.prospects_hit_in_period === 1 ? "company" : "companies"}`}
                to={link("inbox", { show: "all", ...(since ? { since } : {}) })}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Panel fill title="Hits over time" subtitle="By what found the company">
                {points.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Nothing in this period.
                  </Typography>
                ) : (
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="var(--mui-palette-divider)" />
                        <XAxis dataKey="t" tickFormatter={utcDayLabel} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <Tooltip
                          cursor={{ fill: "var(--mui-palette-action-hover)" }}
                          labelFormatter={(t) => utcDayLabel(Number(t))}
                          formatter={(value, name) => [formatNumber(Number(value)), hitKindLabel(name as "signal" | "agent" | "manual")]}
                          contentStyle={{
                            background: "var(--mui-palette-background-paper)",
                            border: "none",
                            boxShadow: "0 4px 16px rgba(var(--mui-palette-text-primaryChannel) / 0.12)",
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="signal" stackId="hits" fill={HIT_KIND_COLORS.signal} isAnimationActive={false} />
                        <Bar dataKey="agent" stackId="hits" fill={HIT_KIND_COLORS.agent} isAnimationActive={false} />
                        <Bar dataKey="manual" stackId="hits" fill={HIT_KIND_COLORS.manual} isAnimationActive={false} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
                <Box sx={{ mt: 2 }}>
                  <SegmentedBar
                    emptyLabel="No hits in this period"
                    segments={(["signal", "agent", "manual"] as const).map((k) => ({
                      key: k,
                      label: `${hitKindLabel(k)}${kindOf(k) ? ` · ${formatNumber(kindOf(k)!.prospects)} ${kindOf(k)!.prospects === 1 ? "company" : "companies"}` : ""}`,
                      value: kindOf(k)?.hits ?? 0,
                      color: HIT_KIND_COLORS[k],
                      to: link("inbox", { show: "all", kind: k, ...(since ? { since } : {}) }),
                    }))}
                  />
                </Box>
              </Panel>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                <Panel title="Where they stand" subtitle="Every company the tenant has prospected">
                  <SegmentedBar
                    emptyLabel="No prospects yet"
                    segments={[
                      { key: "pending", label: "To review", value: data.review.pending, color: "var(--mui-palette-info-main)", to: link("inbox", { show: "review" }) },
                      { key: "accepted", label: "Pursuing", value: data.review.accepted, color: "var(--mui-palette-success-main)", to: link("inbox", { show: "all", review_state: "accepted" }) },
                      { key: "disqualified", label: "Disqualified", value: data.review.disqualified, color: "var(--mui-palette-text-disabled)", to: link("inbox", { show: "disqualified" }) },
                    ]}
                  />
                  {data.queues.new_hits_since_decision > 0 && (
                    <Link component={RouterLink} to={link("inbox", { show: "all", new_hits: "yes" })} variant="body2" sx={{ display: "block", mt: 2 }}>
                      {formatNumber(data.queues.new_hits_since_decision)} decided {data.queues.new_hits_since_decision === 1 ? "company was" : "companies were"} prospected again since
                    </Link>
                  )}
                </Panel>
                <Panel title="Most prospected" subtitle="All time" disablePadding>
                  {data.top.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "text.secondary", p: 2 }}>
                      Nothing yet
                    </Typography>
                  ) : (
                    <Stack>
                      {data.top.map((p) => (
                        <Stack
                          key={p.id}
                          direction="row"
                          spacing={1.5}
                          sx={{ alignItems: "center", px: 2, py: 1, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
                        >
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Link component={RouterLink} to={link("inbox", { show: "all", prospect: p.id })} underline="hover" variant="body2" sx={{ fontWeight: 500 }} noWrap>
                              {p.company_name ?? "Unnamed company"}
                            </Link>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                              {p.company_country && <CountryName code={p.company_country} showName={false} variant="inherit" />}
                              {p.company_country && " · "}
                              last <RelativeTime value={p.last_hit_at} />
                            </Typography>
                          </Box>
                          <StatusChip
                            kind="prospect_status"
                            value={p.done_at ? "done" : p.review_state}
                            variant="outlined"
                          />
                          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "text.secondary", minWidth: 48, justifyContent: "flex-end" }}>
                            {hitKindIcon("signal", { fontSize: "inherit" })}
                            <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                              {formatNumber(p.hit_count)}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Panel>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      )}
    </>
  );
}
